import { useDialog } from '@databases/dialog';
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import { isEmpty } from '@canvas/adf/is-empty';
import { type CommentStore } from '@canvas/comments/comment/store';

import { messages } from '../i18n.ts';

interface UseCloseWithDraftCheckProps {
  handleClose: () => void;
  store: CommentStore;
  threadId: string | undefined;
}

export const useCloseWithDraftCheck = ({
  handleClose,
  store,
  threadId
}: UseCloseWithDraftCheckProps) => {
  const { showDialog } = useDialog();
  const { formatMessage } = useIntl();

  const handleCloseWithDraftCheck = useCallback(() => {
    const newCommentDraft = store.getCommentDraft('new');
    const replyDraft = threadId
      ? store.getCommentDraft(store.getDraftId(threadId))
      : undefined;
    const editDraft = threadId
      ? store.getCommentDraft(store.getEditDraftId(threadId))
      : undefined;

    const hasNewCommentContent = newCommentDraft && !isEmpty(newCommentDraft);
    const hasReplyContent = replyDraft && !isEmpty(replyDraft);
    const hasEditContent = editDraft && !isEmpty(editDraft);
    const hasDraft = hasNewCommentContent || hasReplyContent || hasEditContent;

    if (hasDraft) {
      showDialog('ConfirmDialog', {
        heading: formatMessage(
          messages['comments-confirm-dialog.discard-comment-heading']
        ),
        body: (
          <>{formatMessage(messages['comments-confirm-dialog.discard-comment-body'])}</>
        ),
        yesLabel: formatMessage(
          messages['comments-confirm-dialog.discard-comment-confirm-button']
        ),
        noLabel: formatMessage(
          messages['comments-confirm-dialog.discard-comment-cancel-button']
        ),
        appearance: 'warning',
        width: 'medium',
        onClose: (result) => {
          if (result) {
            if (hasNewCommentContent) {
              store.deleteCommentDraft('new');
            }
            if (hasReplyContent && threadId) {
              store.deleteCommentDraft(store.getDraftId(threadId));
            }
            if (hasEditContent && threadId) {
              store.deleteCommentDraft(store.getEditDraftId(threadId));
            }
            handleClose();
          }
        }
      });
    } else {
      handleClose();
    }
  }, [handleClose, store, threadId, showDialog, formatMessage]);

  return handleCloseWithDraftCheck;
};
